import { getCmsProperty, useGlobalStore } from '@sunrise/shared'
import {
    MLM,
    MsTracking,
    NOT_SET,
    TrackingParameter,
    UserAction,
    createTealiumTracking,
    track,
} from '@sunrise/tracking'
import { Cms } from '@sunrise/types'
import { lowerCase } from 'lodash-es'
import { SimpleNewsItem } from '../../../ts/mapper/simpleNewsItemMapper'
import { ElementTrackingIds } from '../../../ts/tracking/elementTrackingIds'
import { FilterClicks } from './M_Combined_News_Resultlist.types'

const baseTracking: TrackingParameter = {
    mslayer_element_count: NOT_SET,
    mslayer_element_variant: 'native',
    mslayer_element_body_type: 'text_body',
    mslayer_element_id: NOT_SET,
    mslayer_affiliate_id: NOT_SET,
    mslayer_element_company_name: NOT_SET,
    mslayer_element_detail_start_date: NOT_SET,
}

export function tealiumTrackNextPage() {
    track({
        ...baseTracking,
        mslayer_element_data_id: ElementTrackingIds.RSS_FEED_RESULT_LIST_MORE,
        mslayer_element_text: 'RSS_Feed_Weitere',
        mslayer_element_position: NOT_SET,
        mslayer_element_type_text: 'Weitere Nachrichten',
    })
}

export function tealiumTrackFilterModal(type: FilterClicks) {
    track({
        ...baseTracking,
        mslayer_element_data_id: ElementTrackingIds.FILTER_MODAL,
        mslayer_element_text: 'Aktuelles_Nachrichten_Filtermodal',
        mslayer_element_position: NOT_SET,
        mslayer_element_type_text:
            type === FilterClicks.FILTER_OPEN
                ? 'Nachrichten_Filtermodal_oeffnen'
                : 'Nachrichten_Filtermodal_schliessen',
        mslayer_element_variant: type,
        mslayer_element_body_type: 'modal',
    })
}

export function mlmTrackViews(
    items: SimpleNewsItem[],
    currentPage: number,
    allDisturber: Cms.Content[] = [],
    amountOfRenderedItems = 0,
) {
    const disturberPositions = sortedPositionsFrom(allDisturber)

    let position = disturberPositions.reduce((currentPostion, disturberPosition) => {
        if (disturberPosition <= currentPostion) {
            return currentPostion + 1
        }
        return currentPostion
    }, amountOfRenderedItems + 1)

    const mlmItems = items.map((item) => {
        if (disturberPositions.includes(position)) {
            position++
        }

        const mlmItem: MLM.Item = {
            id: item.id.toString(),
            type: normalizeSource('', item.provider),
            typePaid: 'standard',
            typePosition: position,
        }

        position++

        return mlmItem
    })

    MLM.trackView(buildMlmTracking(MLM.ActionId.VIEW, currentPage, mlmItems))
}

function buildMlmTracking(actionId: MLM.ActionId, page: number, items: MLM.Item[]): MLM.TrackingData {
    return {
        affiliateId: 313,
        actionId,
        componentId: 'M_Combined_News_Resultlist',
        itemChannel: 'aktuelles',
        pageUrl: document.URL,
        page,
        items,
    }
}

function normalizeSource(defaultValue: string, text?: string): string {
    if (!text) {
        return defaultValue
    }

    return text
        .toLowerCase()
        .replaceAll('ü', 'ue')
        .replaceAll('ö', 'oe')
        .replaceAll('ä', 'ae')
        .replace(/[^0-9a-zA-Z_]/g, '')
}

export function createUATracking(id: number) {
    return UserAction.createUserActionTracking({
        id,
        channel: 'news',
    })
}

export function trackSort(currentSort: string) {
    track({
        mslayer_internal_searchbox_used: 'userdefined',
        mslayer_list_sorting_logic: lowerCase(currentSort),
    })
}

function createMLMTracking(item: SimpleNewsItem, currentPage: number, position: number): MsTracking {
    return MLM.createMlmTracking({
        affiliateId: 313,
        actionId: MLM.ActionId.CLICK,
        componentId: 'M_Combined_News_Resultlist',
        itemChannel: 'aktuelles',
        pageUrl: useGlobalStore().requestContext?.url.toString() || '',
        page: currentPage,
        items: [
            {
                id: item?.id.toString(),
                type: normalizeSource('', item.provider),
                typePaid: 'standard',
                typePosition: position,
            },
        ],
    })
}

function createTealiumClickTracking(item: SimpleNewsItem, position: number): MsTracking {
    return createTealiumTracking({
        ...baseTracking,
        mslayer_element_data_id: ElementTrackingIds.RSS_FEED_RESULT_LIST,
        mslayer_element_text: 'RSS_Feed_Meldung',
        mslayer_element_position: position,
        mslayer_element_company_name: normalizeSource(NOT_SET, item.provider),
        mslayer_element_type_text: 'Nachrichten Klick',
    })
}

function createTrackingFor(item: SimpleNewsItem, currentPage: number, position: number): MsTracking[] {
    return [
        createUATracking(item.id),
        createMLMTracking(item, currentPage, position),
        createTealiumClickTracking(item, position),
    ]
}

export function createTrackings(
    item: SimpleNewsItem[],
    allDisturber: Cms.Content[] = [],
    size: number,
): Record<number, MsTracking[]> {
    const disturberPositions = sortedPositionsFrom(allDisturber)
    let currentPage = 1
    let position = 1
    return item.reduce((acc: any, item, index) => {
        if (shouldPageUp(index, size)) {
            currentPage++
        }

        if (disturberPositions.includes(position)) {
            position++
        }

        acc[item.id] = createTrackingFor(item, currentPage, position)

        position++

        return acc
    }, {})
}

function shouldPageUp(index: number, size: number) {
    return index > 0 && index % size === 0
}

function sortedPositionsFrom(allDisturber: Cms.Content[]): number[] {
    return allDisturber
        .map((item) => getCmsProperty<number>('meinestadt/types#resultlist_disturber_position', item))
        .filter((id): id is number => id !== undefined)
        .sort((a, b) => a - b)
}
